import React, { useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { useOutletContext, useParams } from "react-router-dom";
import { FaPlus, FaTrash, FaUpload } from "react-icons/fa";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db } from "../firebase/firebase";
import "./ImageGalleryComponent.scss";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";

const WG_TOTAL_SLOTS = 12;
const WG_TRIAL_LIMIT = 3;
const WG_MAX_FILE_SIZE = 4 * 1024 * 1024;
const WG_MAX_ORIGINAL_FILE_SIZE = 20 * 1024 * 1024;
const WG_COMPRESSED_TARGET_SIZE = 1.2 * 1024 * 1024;
const WG_MAX_DIMENSION = 2200;

const WG_SECTIONS = [
  {
    key: "signatureBannerImage",
    title: "Signature Banner Image",
    subtitle: "Main image used in the landing page hero section.",
    slots: [{ key: "signatureBannerImage", label: "Banner Image", index: 0 }],
  },
  {
    key: "rsvpSectionImage",
    title: "RSVP Section Image",
    subtitle: "Image used in the RSVP component.",
    slots: [{ key: "rsvpSectionImage", label: "RSVP Image", index: 1 }],
  },
  {
    key: "loveNoteImage",
    title: "Love Note Image",
    subtitle: "Image used in the love note section.",
    slots: [{ key: "loveNoteImage", label: "Love Note Image", index: 2 }],
  },
  {
    key: "momentsOfLove",
    title: "Moments of Love",
    subtitle: "Upload 9 images for your moments of love gallery.",
    slots: Array.from({ length: 9 }, (_, i) => ({
      key: `moment_${i + 1}`,
      label: `Moment ${i + 1}`,
      index: i + 3,
    })),
  },
];



const createEmptyGalleryState = () => ({
  signatureBannerImage: "",
  rsvpSectionImage: "",
  loveNoteImage: "",
  momentsOfLove: Array(9).fill(""),
});

const createEmptyGalleryPaths = () => ({
  signatureBannerImage: "",
  rsvpSectionImage: "",
  loveNoteImage: "",
  momentsOfLove: Array(9).fill(""),
});

const ImageGalleryComponent = () => {
  const { slug } = useParams();
  const { weddingData } = useOutletContext();
  const storage = getStorage();

  const fileInputRefs = useRef([]);

  const isTrialPlan =
    weddingData?.isTrial === true || weddingData?.plan === "trial";
  const allowedSlots = isTrialPlan ? WG_TRIAL_LIMIT : WG_TOTAL_SLOTS;
  const ownerUid = weddingData?.uid || weddingData?.ownerUid || "";

  const initialGallery = useMemo(() => {
    const emptyState = createEmptyGalleryState();
    const savedGallery = weddingData?.imageGallery;

    if (
      savedGallery &&
      !Array.isArray(savedGallery) &&
      typeof savedGallery === "object"
    ) {
      return {
        signatureBannerImage: savedGallery?.signatureBannerImage || "",
        rsvpSectionImage: savedGallery?.rsvpSectionImage || "",
        loveNoteImage: savedGallery?.loveNoteImage || "",
        momentsOfLove: Array.from({ length: 9 }, (_, index) => {
          return savedGallery?.momentsOfLove?.[index] || "";
        }),
      };
    }

    if (Array.isArray(savedGallery)) {
      return {
        signatureBannerImage: savedGallery[0]?.image || "",
        rsvpSectionImage: savedGallery[1]?.image || "",
        loveNoteImage: savedGallery[2]?.image || "",
        momentsOfLove: Array.from({ length: 9 }, (_, index) => {
          return savedGallery[index + 3]?.image || "";
        }),
      };
    }

    return emptyState;
  }, [weddingData]);

  const initialGalleryPaths = useMemo(() => {
    const emptyPaths = createEmptyGalleryPaths();
    const savedPaths = weddingData?.imageGalleryPaths;

    if (
      savedPaths &&
      !Array.isArray(savedPaths) &&
      typeof savedPaths === "object"
    ) {
      return {
        signatureBannerImage: savedPaths?.signatureBannerImage || "",
        rsvpSectionImage: savedPaths?.rsvpSectionImage || "",
        loveNoteImage: savedPaths?.loveNoteImage || "",
        momentsOfLove: Array.from({ length: 9 }, (_, index) => {
          return savedPaths?.momentsOfLove?.[index] || "";
        }),
      };
    }

    return emptyPaths;
  }, [weddingData]);

  const [galleryData, setGalleryData] = useState(initialGallery);
  const [galleryPaths, setGalleryPaths] = useState(initialGalleryPaths);
  const [showImages, setShowImages] = useState(
    weddingData?.showImages !== false
  );
  const [isSavingShowImages, setIsSavingShowImages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [slotErrors, setSlotErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [pendingUploadIndexes, setPendingUploadIndexes] = useState({});
  const [compressingIndexes, setCompressingIndexes] = useState({});
  const [compressionInfo, setCompressionInfo] = useState({});
  const [dragActiveIndex, setDragActiveIndex] = useState(null);
  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  useEffect(() => {
    setGalleryData(initialGallery);
  }, [initialGallery]);

  useEffect(() => {
    setGalleryPaths(initialGalleryPaths);
  }, [initialGalleryPaths]);

  useEffect(() => {
    setShowImages(weddingData?.showImages !== false);
  }, [weddingData?.showImages]);

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);

        if (!slug) {
          if (isMounted) setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", slug));

        if (!isMounted) return;

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading site theme:", error);

        if (isMounted) {
          setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
        }
      } finally {
        if (isMounted) {
          setThemeLoading(false);
        }
      }
    };

    loadTheme();

    return () => {
      isMounted = false;
    };
  }, [slug, weddingData]);

  const getImageByIndex = (index, source = galleryData) => {
    if (index === 0) return source.signatureBannerImage || "";
    if (index === 1) return source.rsvpSectionImage || "";
    if (index === 2) return source.loveNoteImage || "";
    return source.momentsOfLove[index - 3] || "";
  };

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  const getPathByIndex = (index, source = galleryPaths) => {
    if (index === 0) return source.signatureBannerImage || "";
    if (index === 1) return source.rsvpSectionImage || "";
    if (index === 2) return source.loveNoteImage || "";
    return source.momentsOfLove[index - 3] || "";
  };

  const setImageByIndex = (source, index, value) => {
    if (index === 0) return { ...source, signatureBannerImage: value };
    if (index === 1) return { ...source, rsvpSectionImage: value };
    if (index === 2) return { ...source, loveNoteImage: value };

    const updatedMoments = [...source.momentsOfLove];
    updatedMoments[index - 3] = value;
    return { ...source, momentsOfLove: updatedMoments };
  };

  const setPathByIndex = (source, index, value) => {
    if (index === 0) return { ...source, signatureBannerImage: value };
    if (index === 1) return { ...source, rsvpSectionImage: value };
    if (index === 2) return { ...source, loveNoteImage: value };

    const updatedMoments = [...source.momentsOfLove];
    updatedMoments[index - 3] = value;
    return { ...source, momentsOfLove: updatedMoments };
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const setSlotError = (index, message) => {
    setSlotErrors((prev) => ({
      ...prev,
      [index]: message,
    }));
  };

  const clearSlotError = (index) => {
    setSlotErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const markPendingUpload = (index, value) => {
    setPendingUploadIndexes((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const clearPendingUpload = (index) => {
    setPendingUploadIndexes((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const setCompressing = (index, value) => {
    setCompressingIndexes((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const clearCompressing = (index) => {
    setCompressingIndexes((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const setCompressionMeta = (index, info) => {
    setCompressionInfo((prev) => ({
      ...prev,
      [index]: info,
    }));
  };

  const clearCompressionMeta = (index) => {
    setCompressionInfo((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const persistGallery = async (
    nextGalleryData,
    nextGalleryPaths,
    successText = "Image gallery updated successfully."
  ) => {
    const structuredGallery = {
      signatureBannerImage: nextGalleryData.signatureBannerImage || "",
      rsvpSectionImage: nextGalleryData.rsvpSectionImage || "",
      loveNoteImage: nextGalleryData.loveNoteImage || "",
      momentsOfLove: Array.from({ length: 9 }, (_, index) => {
        return nextGalleryData.momentsOfLove[index] || "";
      }),
    };

    const structuredPaths = {
      signatureBannerImage: nextGalleryPaths.signatureBannerImage || "",
      rsvpSectionImage: nextGalleryPaths.rsvpSectionImage || "",
      loveNoteImage: nextGalleryPaths.loveNoteImage || "",
      momentsOfLove: Array.from({ length: 9 }, (_, index) => {
        return nextGalleryPaths.momentsOfLove[index] || "";
      }),
    };

    await updateDoc(doc(db, "sites", slug), {
      imageGallery: structuredGallery,
      imageGalleryPaths: structuredPaths,
      updatedAt: serverTimestamp(),
    });

    setSuccessMessage(successText);
  };

  const handleToggleShowImages = async () => {
    const nextValue = !showImages;
    const previousValue = showImages;

    try {
      setShowImages(nextValue);
      setIsSavingShowImages(true);
      setSuccessMessage("");

      const updatePayload = {
        showImages: nextValue,
        updatedAt: serverTimestamp(),
      };

      const promises = [updateDoc(doc(db, "sites", slug), updatePayload)];

      if (ownerUid) {
        promises.push(updateDoc(doc(db, "users", ownerUid), updatePayload));
      }

      await Promise.all(promises);

      setSuccessMessage(
        nextValue
          ? "Image visibility turned on successfully."
          : "Image visibility turned off successfully."
      );
    } catch (error) {
      console.error("Failed to update showImages:", error);
      setShowImages(previousValue);
      alert("Failed to update image visibility. Please try again.");
    } finally {
      setIsSavingShowImages(false);
    }
  };

  const loadImageFromFile = (file) =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(image);
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("The selected image is invalid or corrupted."));
      };

      image.src = objectUrl;
    });

  const canvasToBlob = (canvas, mimeType, quality) =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to compress image."));
            return;
          }
          resolve(blob);
        },
        mimeType,
        quality
      );
    });

  const getSafeOutputMimeType = (file) => {
    if (file.type === "image/png") return "image/png";
    if (file.type === "image/webp") return "image/webp";
    return "image/jpeg";
  };

  const getOutputExtension = (mimeType) => {
    if (mimeType === "image/png") return "png";
    if (mimeType === "image/webp") return "webp";
    return "jpg";
  };

  const compressImage = async (file) => {
    const image = await loadImageFromFile(file);

    let width = image.naturalWidth || image.width;
    let height = image.naturalHeight || image.height;

    if (!width || !height) {
      throw new Error("The selected image is invalid or corrupted.");
    }

    const longestSide = Math.max(width, height);
    if (longestSide > WG_MAX_DIMENSION) {
      const ratio = WG_MAX_DIMENSION / longestSide;
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    const canvas = document.createElement("canvas");
    let context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Image compression is not supported in this browser.");
    }

    canvas.width = width;
    canvas.height = height;
    context.drawImage(image, 0, 0, width, height);

    const outputMimeType = getSafeOutputMimeType(file);
    const shouldUseQuality = outputMimeType !== "image/png";

    let bestBlob;

    if (outputMimeType === "image/png") {
      bestBlob = await canvasToBlob(canvas, outputMimeType);
    } else {
      let quality = 0.9;
      bestBlob = await canvasToBlob(canvas, outputMimeType, quality);

      while (bestBlob.size > WG_COMPRESSED_TARGET_SIZE && quality > 0.45) {
        quality -= 0.08;
        bestBlob = await canvasToBlob(canvas, outputMimeType, quality);
      }

      let resizeStep = 0;
      while (
        bestBlob.size > WG_MAX_FILE_SIZE &&
        resizeStep < 3 &&
        canvas.width > 900 &&
        canvas.height > 900
      ) {
        resizeStep += 1;
        const scaledWidth = Math.round(canvas.width * 0.88);
        const scaledHeight = Math.round(canvas.height * 0.88);

        const resizedCanvas = document.createElement("canvas");
        const resizedContext = resizedCanvas.getContext("2d");

        if (!resizedContext) break;

        resizedCanvas.width = scaledWidth;
        resizedCanvas.height = scaledHeight;
        resizedContext.drawImage(canvas, 0, 0, scaledWidth, scaledHeight);

        canvas.width = scaledWidth;
        canvas.height = scaledHeight;
        context = canvas.getContext("2d");

        if (!context) break;

        context.drawImage(resizedCanvas, 0, 0, scaledWidth, scaledHeight);

        let nextQuality = 0.82;
        bestBlob = await canvasToBlob(canvas, outputMimeType, nextQuality);

        while (bestBlob.size > WG_COMPRESSED_TARGET_SIZE && nextQuality > 0.45) {
          nextQuality -= 0.08;
          bestBlob = await canvasToBlob(canvas, outputMimeType, nextQuality);
        }
      }
    }

    const finalBlob = bestBlob.size < file.size ? bestBlob : file;

    const outputFile =
      finalBlob === file
        ? file
        : new File(
          [finalBlob],
          file.name.replace(/\.[^.]+$/, "") +
          `.${getOutputExtension(outputMimeType)}`,
          {
            type: outputMimeType,
            lastModified: Date.now(),
          }
        );

    return {
      file: outputFile,
      originalSize: file.size,
      compressedSize: outputFile.size,
      wasCompressed: outputFile.size < file.size,
      width: canvas.width,
      height: canvas.height,
      mimeType: outputFile.type,
      usedQualityControl: shouldUseQuality,
    };
  };

  const getUploadErrorMessage = (error, file) => {
    const message = error?.message || "";
    const code = error?.code || "";

    if (message.includes("Only premium users")) return message;
    if (message.includes("not an image")) return message;
    if (message.includes("invalid or corrupted")) return message;

    if (code === "storage/unauthorized") {
      return "Upload failed because Firebase Storage rules are blocking this upload.";
    }

    if (code === "storage/canceled") {
      return "Image upload was canceled.";
    }

    if (code === "storage/retry-limit-exceeded") {
      return "Upload took too long. Please try again.";
    }

    if (
      code === "resource-exhausted" ||
      code === "invalid-argument" ||
      message.toLowerCase().includes("too large")
    ) {
      return `This image is too large to upload. Current file size: ${formatFileSize(
        file?.size || 0
      )}.`;
    }

    return "This image could not be uploaded. Please try again.";
  };

  const triggerFileInput = (index) => {
    if (index >= allowedSlots) {
      setSlotError(
        index,
        `Only premium users can use more than ${WG_TRIAL_LIMIT} image slots.`
      );
      setSuccessMessage("");
      return;
    }

    if (loading) return;

    clearSlotError(index);
    fileInputRefs.current[index]?.click();
  };

  const processUpload = async (originalFile, index) => {
    if (!originalFile) return;

    clearSlotError(index);
    clearCompressionMeta(index);
    setSuccessMessage("");

    if (index >= allowedSlots) {
      setSlotError(
        index,
        `Only premium users can use more than ${WG_TRIAL_LIMIT} image slots.`
      );
      return;
    }

    if (!originalFile.type.startsWith("image/")) {
      setSlotError(
        index,
        "The selected file is not an image. Please choose JPG, PNG, or WEBP."
      );
      return;
    }

    if (originalFile.size > WG_MAX_ORIGINAL_FILE_SIZE) {
      setSlotError(
        index,
        `This image is too large (${formatFileSize(
          originalFile.size
        )}). Please choose an image smaller than 20 MB.`
      );
      return;
    }

    const previousGalleryData = {
      ...galleryData,
      momentsOfLove: [...galleryData.momentsOfLove],
    };
    const previousGalleryPaths = {
      ...galleryPaths,
      momentsOfLove: [...galleryPaths.momentsOfLove],
    };
    const oldStoragePath = getPathByIndex(index, galleryPaths);

    try {
      setLoading(true);
      setUploadingIndex(index);
      setDeletingIndex(null);
      markPendingUpload(index, true);
      setCompressing(index, true);
      setUploadProgress((prev) => ({ ...prev, [index]: 0 }));

      const compressionResult = await compressImage(originalFile);
      const uploadFile = compressionResult.file;

      setCompressionMeta(index, {
        originalSize: compressionResult.originalSize,
        compressedSize: compressionResult.compressedSize,
        wasCompressed: compressionResult.wasCompressed,
      });

      if (uploadFile.size > WG_MAX_FILE_SIZE) {
        throw new Error(
          `This image is still too large after compression (${formatFileSize(
            uploadFile.size
          )}). Please use a smaller image.`
        );
      }

      clearCompressing(index);

      const extension =
        uploadFile.name?.split(".").pop()?.toLowerCase() ||
        uploadFile.type?.split("/")[1] ||
        "jpg";

      const storagePath = `sites/${slug}/gallery/${Date.now()}-${index}.${extension}`;
      const imageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(imageRef, uploadFile);

      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            setUploadProgress((prev) => ({
              ...prev,
              [index]: progress,
            }));
          },
          (error) => reject(error),
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            } catch (error) {
              reject(error);
            }
          }
        );
      });

      const updatedGalleryData = setImageByIndex(galleryData, index, downloadURL);
      const updatedGalleryPaths = setPathByIndex(galleryPaths, index, storagePath);

      await persistGallery(
        updatedGalleryData,
        updatedGalleryPaths,
        `Image ${index + 1} uploaded successfully.`
      );

      if (oldStoragePath) {
        try {
          await deleteObject(ref(storage, oldStoragePath));
        } catch (cleanupError) {
          console.error("Old image cleanup failed:", cleanupError);
        }
      }

      setGalleryData(updatedGalleryData);
      setGalleryPaths(updatedGalleryPaths);
      clearSlotError(index);
    } catch (error) {
      console.error("Device upload error:", error);
      setGalleryData(previousGalleryData);
      setGalleryPaths(previousGalleryPaths);
      setSlotError(index, getUploadErrorMessage(error, originalFile));
    } finally {
      setLoading(false);
      setUploadingIndex(null);
      clearPendingUpload(index);
      clearCompressing(index);
      setUploadProgress((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  const handleDeviceUpload = async (event, index) => {
    const originalFile = event.target.files?.[0];
    if (!originalFile) return;

    await processUpload(originalFile, index);
    event.target.value = "";
  };

  const handleDropUpload = async (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActiveIndex(null);

    if (loading) return;

    const originalFile = event.dataTransfer?.files?.[0];
    if (!originalFile) return;

    await processUpload(originalFile, index);
  };

  const handleDragEnter = (event, index) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading || index >= allowedSlots) return;
    setDragActiveIndex(index);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading || index >= allowedSlots) return;
    if (dragActiveIndex !== index) {
      setDragActiveIndex(index);
    }
  };

  const handleDragLeave = (event, index) => {
    event.preventDefault();
    event.stopPropagation();

    const relatedTarget = event.relatedTarget;
    if (event.currentTarget.contains(relatedTarget)) return;

    if (dragActiveIndex === index) {
      setDragActiveIndex(null);
    }
  };

  const handleDeleteSlot = async (index) => {
    if (index >= allowedSlots) {
      setSlotError(
        index,
        `Only premium users can use more than ${WG_TRIAL_LIMIT} image slots.`
      );
      setSuccessMessage("");
      return;
    }

    if (!getImageByIndex(index)) return;

    const previousGalleryData = {
      ...galleryData,
      momentsOfLove: [...galleryData.momentsOfLove],
    };
    const previousGalleryPaths = {
      ...galleryPaths,
      momentsOfLove: [...galleryPaths.momentsOfLove],
    };
    const oldStoragePath = getPathByIndex(index, galleryPaths);

    try {
      setLoading(true);
      setDeletingIndex(index);
      setUploadingIndex(null);
      setSuccessMessage("");
      clearSlotError(index);
      clearCompressionMeta(index);

      const updatedGalleryData = setImageByIndex(galleryData, index, "");
      const updatedGalleryPaths = setPathByIndex(galleryPaths, index, "");

      await persistGallery(
        updatedGalleryData,
        updatedGalleryPaths,
        `Image ${index + 1} deleted successfully.`
      );

      if (oldStoragePath) {
        try {
          await deleteObject(ref(storage, oldStoragePath));
        } catch (cleanupError) {
          console.error("Delete storage object failed:", cleanupError);
        }
      }

      setGalleryData(updatedGalleryData);
      setGalleryPaths(updatedGalleryPaths);
    } catch (error) {
      console.error("Delete image error:", error);
      setGalleryData(previousGalleryData);
      setGalleryPaths(previousGalleryPaths);
      setSlotError(index, "Failed to delete image. Please try again.");
    } finally {
      setLoading(false);
      setDeletingIndex(null);
    }
  };

  const renderSlotCard = (slot) => {
    const slotImage = getImageByIndex(slot.index);
    const hasValue = !!slotImage;
    const isLocked = slot.index >= allowedSlots;
    const isUploadingThis = uploadingIndex === slot.index;
    const isDeletingThis = deletingIndex === slot.index;
    const slotError = slotErrors[slot.index];
    const slotProgress = uploadProgress[slot.index] || 0;
    const isPendingUpload = !!pendingUploadIndexes[slot.index];
    const isDragActive = dragActiveIndex === slot.index;

    if (themeLoading || !siteTheme) return null;
    return (
      <div
        className={`wedGalleryEditor__item ${isLocked ? "wedGalleryEditor__item--locked" : ""
          }`}
        key={slot.key}
      >
        <div className="wedGalleryEditor__itemHeader">
          <div className="wedGalleryEditor__number">{slot.index + 1}</div>
          {isLocked && (
            <span className="wedGalleryEditor__lockedBadge">Premium</span>
          )}
        </div>

        <div className="wedGalleryEditor__slotLabel">{slot.label}</div>

        <input
          ref={(el) => {
            fileInputRefs.current[slot.index] = el;
          }}
          id={`wg-file-${slot.index}`}
          type="file"
          accept="image/*"
          className="wedGalleryEditor__fileInput"
          onChange={(e) => handleDeviceUpload(e, slot.index)}
          hidden
        />

        <div
          className={`wedGalleryEditor__previewWrap ${isDragActive ? "wedGalleryEditor__previewWrap--dragActive" : ""
            } ${isLocked ? "wedGalleryEditor__previewWrap--locked" : ""}`}
          onDragEnter={(e) => handleDragEnter(e, slot.index)}
          onDragOver={(e) => handleDragOver(e, slot.index)}
          onDragLeave={(e) => handleDragLeave(e, slot.index)}
          onDrop={(e) => handleDropUpload(e, slot.index)}
          onClick={() => {
            if (!loading && !isLocked) {
              triggerFileInput(slot.index);
            }
          }}
          role="button"
          tabIndex={isLocked ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !loading && !isLocked) {
              e.preventDefault();
              triggerFileInput(slot.index);
            }
          }}
          aria-label={`Upload image for ${slot.label}`}
        >
          {isPendingUpload ? (
            <div className="wedGalleryEditor__uploadState">
              <div className="wedGalleryEditor__uploadText">
                Compressing image…
              </div>

              <div className="wedGalleryEditor__progressTrack">
                <div
                  className="wedGalleryEditor__progressFill"
                  style={{ width: `${slotProgress}%` }}
                />
              </div>
              <div className="wedGalleryEditor__progressValue">
                {slotProgress}%
              </div>
            </div>
          ) : hasValue ? (
            <>
              <img
                src={slotImage}
                alt={slot.label}
                className="wedGalleryEditor__preview"
              />
              <div className="wedGalleryEditor__dropOverlay">
                <FaUpload />
                <span>Drag & drop to replace</span>
              </div>
            </>
          ) : (
            <div className="wedGalleryEditor__placeholder">
              <FaUpload className="wedGalleryEditor__placeholderIcon" />
              <span>Drag & drop image here</span>
              <small>or click to upload</small>
            </div>
          )}
        </div>

        <div className="wedGalleryEditor__actions">
          <button
            type="button"
            className="wedGalleryEditor__btn wedGalleryEditor__btn--edit"
            onClick={() => triggerFileInput(slot.index)}
            disabled={loading || isLocked}
            aria-label={hasValue ? `Change ${slot.label}` : `Add ${slot.label}`}
            title={hasValue ? `Change ${slot.label}` : `Add ${slot.label}`}
          >
            <FaPlus />
          </button>

          <button
            type="button"
            className="wedGalleryEditor__btn wedGalleryEditor__btn--delete"
            onClick={() => handleDeleteSlot(slot.index)}
            disabled={!hasValue || loading || isLocked || isPendingUpload}
            aria-label={`Delete ${slot.label}`}
            title={`Delete ${slot.label}`}
          >
            <FaTrash />
          </button>
        </div>

        {(isUploadingThis || isDeletingThis) && !isPendingUpload && (
          <p className="wedGalleryEditor__slotStatus">
            {isUploadingThis ? "Uploading..." : "Deleting..."}
          </p>
        )}

        {slotError && <p className="wedGalleryEditor__slotError">{slotError}</p>}
      </div>
    );
  };

  return (
    <div className="wedGalleryEditor" style={themeStyle}>
      <div className="wedGalleryEditor__card">
        <div className="wedGalleryEditor__header">
          <p className="wedGalleryEditor__eyebrow">Wedding Gallery</p>
          <h1 className="wedGalleryEditor__title">Image Gallery</h1>
          <p className="wedGalleryEditor__subtitle">
            Organize your images by section so they can be used directly in your
            website components.
          </p>
        </div>

        <div className="wedGalleryEditor__toggleCard">
          <div className="wedGalleryEditor__toggleContent">
            <div className="wedGalleryEditor__toggleTextWrap">
              <h3 className="wedGalleryEditor__toggleTitle">Show Images</h3>
              <p className="wedGalleryEditor__toggleText">
                Turn gallery images on or off for this wedding website.
              </p>
            </div>

            <button
              type="button"
              className={`wedGalleryEditor__toggle ${showImages ? "wedGalleryEditor__toggle--active" : ""
                }`}
              onClick={handleToggleShowImages}
              disabled={isSavingShowImages}
              aria-pressed={showImages}
              aria-label="Toggle show images"
            >
              <span className="wedGalleryEditor__toggleTrack">
                <span className="wedGalleryEditor__toggleThumb" />
              </span>
              <span className="wedGalleryEditor__toggleLabel">
                {isSavingShowImages
                  ? "Saving..."
                  : showImages
                    ? "Enabled"
                    : "Disabled"}
              </span>
            </button>
          </div>
        </div>

        <div className="wedGalleryEditor__noticeWrap">
          <div className="wedGalleryEditor__notice wedGalleryEditor__notice--info">
            <strong>Premium feature:</strong> Trial users can add only{" "}
            {WG_TRIAL_LIMIT} images. Premium users can add all {WG_TOTAL_SLOTS} images.
          </div>

          <div className="wedGalleryEditor__notice wedGalleryEditor__notice--soft">
            Supported image types: JPG, PNG, WEBP. Images are automatically
            compressed before upload. Final upload size limit: 4 MB each.
          </div>

          <div className="wedGalleryEditor__notice wedGalleryEditor__notice--soft">
            You can now drag and drop images directly into any image box.
          </div>

          {isTrialPlan && (
            <div className="wedGalleryEditor__notice wedGalleryEditor__notice--soft">
              Trial access includes the first 3 section images only. The
              Moments of Love gallery is available on premium.
            </div>
          )}
        </div>

        <div className="wedGalleryEditor__sections">
          {WG_SECTIONS.map((section) => (
            <section className="wedGalleryEditor__section" key={section.key}>
              <div className="wedGalleryEditor__sectionHead">
                <h2 className="wedGalleryEditor__sectionTitle">
                  {section.title}
                </h2>
                <p className="wedGalleryEditor__sectionSubtitle">
                  {section.subtitle}
                </p>
              </div>

              <div
                className={`wedGalleryEditor__grid ${section.key === "momentsOfLove"
                  ? "wedGalleryEditor__grid--moments"
                  : "wedGalleryEditor__grid--single"
                  }`}
              >
                {section.slots.map(renderSlotCard)}
              </div>
            </section>
          ))}
        </div>

        {successMessage && (
          <p className="wedGalleryEditor__message wedGalleryEditor__message--success">
            {successMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default ImageGalleryComponent;